import { Outlet, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';

import { GameStateProvider } from '../../contexts/game';
import { AuthRequired, AccessDenied } from '../../components/authentication';
import { GameDocumentProvider } from '../../contexts/game-document';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorBoundaryFallback } from '../../components/error-boundary-fallback';
import {
  ROLES,
  canUserAccessGame,
  hasRequiredRole
} from '../../utils/auth-roles';
import { useGame } from '../../hooks/use-game';
import { Spinner } from '../../components/spinner';
import { TimersProvider } from '../../contexts/timer';

const LeaderboardFacilitatorGuard = ({
  gameCode,
  children
}: {
  gameCode: string;
  children: React.ReactNode;
}) => {
  const auth = useAuth();
  const game = useGame(gameCode, true);

  if (game.isLoading) {
    return <Spinner />;
  }

  const canAccess = canUserAccessGame(auth.user, game.data?.organisationId);
  if (!canAccess) {
    return (
      <AccessDenied message="You do not have permission to access this game. Your organisation does not match the game's organisation." />
    );
  }

  return <>{children}</>;
};

/**
 * The layout for private pages.
 * @returns
 */
export const LeaderboardLayout = ({
  isFacilitator: isFacilitatorProp
}: {
  isFacilitator?: boolean;
}) => {
  const { gameCode } = useParams();
  const auth = useAuth();
  const isFacilitatorUser = hasRequiredRole(auth.user, [
    ROLES.FACILITATOR_PORTAL
  ]);
  const isFacilitator = isFacilitatorProp ?? isFacilitatorUser;

  return (
    <AuthRequired
      allowedRoles={isFacilitatorProp ? [ROLES.FACILITATOR_PORTAL] : undefined}>
      <ErrorBoundary
        fallbackRender={(props) => (
          <ErrorBoundaryFallback isFacilitator={isFacilitator} {...props} />
        )}>
        {isFacilitator ? (
          <LeaderboardFacilitatorGuard gameCode={gameCode!}>
            <GameDocumentProvider gameCode={gameCode!} isFromFacilitator>
              <TimersProvider gameCode={gameCode!} isFromFacilitator>
                <GameStateProvider gameCode={gameCode!} isFromFacilitator>
                  <div className={'app-main'}>
                    <Outlet />
                  </div>
                </GameStateProvider>
              </TimersProvider>
            </GameDocumentProvider>
          </LeaderboardFacilitatorGuard>
        ) : (
          <GameDocumentProvider gameCode={gameCode!}>
            <TimersProvider gameCode={gameCode!}>
              <GameStateProvider gameCode={gameCode!} isFromFacilitator={false}>
                <div className={'app-main'}>
                  <Outlet />
                </div>
              </GameStateProvider>
            </TimersProvider>
          </GameDocumentProvider>
        )}
      </ErrorBoundary>
    </AuthRequired>
  );
};

export default LeaderboardLayout;
